import React, { useEffect } from 'react'
import { runInAction } from 'mobx'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { Space, Tag } from 'antd'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'
import { useStores } from '../../../cls/stores/MobXProvider'
import {
	actSaveNotification,
	requireNotificationById
} from '../../../stores/notifications/action'

import { actLoadNotifications } from '../../../stores/ui-lst-notification/actions'

import {
	StatusTag,
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable
} from '../../../components/Shared'
import { Link } from 'react-router-dom'
import ProgressInput from '../../../components/Form/ProgressInput'
import { PLAN_FOR_DELETION_NR_OF_DAYS } from '../../../config'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import { useSmartTranslation } from '../../../cls/TranslationContext'
/**
 * @function LstItem
 * @description Renders a single notification list item row with progress status, metadata, and actions.
 */
const LstItem = observer(({ id, isFirst }) => {
	const { t } = useSmartTranslation()
	const { users, appType } = useStores()
	const { loggedInUser } = users
	const { appIsClient, appIsTeam } = appType
	const notification = requireNotificationById(id)
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const selfPermission =
		notification.createdBy === loggedInUser.user_id ||
		notification.for_user_id === loggedInUser.user_id ||
		isAdmin ||
		isSuperAdmin

	// Function to get the route based on the notification's entity table
	const getRouteForEntity = () => {
		let baseRoute = notification.entity_table
		if (notification.entity_table == 'sprints_team') baseRoute = 'sprints'
		return `${baseRoute}/${notification.entity_uid}/view`
	}

	// Function to save progress of the notification
	const _doSave = (value) => {
		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['notifications'], 'day')
			.unix()

		// If the progress is 'read', mark the notification for deletion at timestamp
		const timestamp = value === 'read' ? plan_for_deletion : false

		runInAction(() => {
			notification.setProgress(value)
			notification.setToDelete(timestamp)
		})
		actSaveNotification(id, notification).catch(console.error)
	}

	return (
		<tr>
			<td>
				<Space style={{ width: '100%' }}>
					<StatusTag
						id={isFirst ? 'target-itm-status' : ''}
						isShort={true}
						tag={notification.progress}
						tagType={'NotificationStatusTag'}
						style={{
							marginRight: 0,
							padding: '0 3px'
						}}
					/>

					<Link
						id={isFirst ? 'target-itm-link' : ''}
						to={getRouteForEntity()}
						onClick={() => {
							if (loggedInUser.isAdmin) {
								runInAction(() => {
									if (
										notification.entity_table ==
											'sprints_team' &&
										appIsClient
									) {
										appType.setAppType('team')
									} else if (
										notification.entity_table ==
											'sprints' &&
										appIsTeam
									) {
										appType.setAppType('client')
									}
								})
							}
						}}
					>
						<UserString
							userId={notification.entity_uid}
							path="name"
							isForNotification={true}
							entity_table={notification.entity_table}
						/>
					</Link>
				</Space>
			</td>
			<td>
				<Space size={0}>
					<Tag id={isFirst ? 'target-itm-table' : ''} color="default">
						{notification.entity_table}
					</Tag>

					<StatusTag
						id={isFirst ? 'target-itm-kind' : ''}
						isShort={false}
						tag={notification.kind}
						tagType={'NotificationTypeTag'}
						style={{
							marginRight: 0,
							padding: '0 3px'
						}}
					/>
				</Space>
			</td>
			<td id={isFirst ? 'target-itm-info' : ''}>
				<CustomTable
					style={{ maxWidth: '200px' }}
					data={[
						[
							t('__created-for'),
							{
								item: (
									<UserString
										userId={notification.for_user_id}
										path="name"
									/>
								)
							}
						],
						[
							t('single.created-at'),
							{
								item: (
									<FormatedTimestamp
										timestamp={notification.createdTs}
										formatedAs="DD.MM.YYYY hh:mm"
									/>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<Space
					style={{ width: '100%' }}
					direction="vertical"
					id={isFirst ? 'target-itm-actions' : ''}
				>
					<ProgressInput
						disabled={
							!selfPermission &&
							!users.canUser('write', 'monthly_reports')
						}
						type="notification"
						value={notification.progress}
						onChange={(value) => _doSave(value)}
					/>
					{
						// Check if the user has permission to view delete information
						// (either through selfPermission or a specific permission)
						// and if the notification has a timestamp for deletion
						(selfPermission ||
							users.canUser('delete_view', 'notifications')) &&
							notification.to_delete && (
								<>
									<span>
										{t('single.delete-on')}
										<FormatedTimestamp
											timestamp={notification.to_delete}
										/>
									</span>
								</>
							)
					}
				</Space>
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

/**
 * @function NotificationsLst
 * @description Main component that renders the full notification list view, including filters, data table, and tour triggers.
 */
const NotificationsLst = observer(() => {
	const { t } = useSmartTranslation()
	const { uiLstNotifications, appType, users, tourStore } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const byDel = uiLstNotifications.del
	const byEntityTable = uiLstNotifications.filter.entity_table
	const byKind = uiLstNotifications.filter.kind
	const byProgress = uiLstNotifications.filter.progress
	const byShowAll = uiLstNotifications.filter.show_all

	// Load notifications on filter changes
	/**
	 * @function useEffect
	 * @description Loads notifications from the server when any of the UI store filters or app type change.
	 */
	useEffect(() => {
		actLoadNotifications()
	}, [byDel, byEntityTable, byKind, byProgress, byShowAll, appType.appIs])

	const notificationLst = getTourArrayByKeys('notificationLst', ['all'])
	const tourFilter = [
		...getTourArrayByKeys('filter', ['section', 'progress']),
		{
			title: t('__filter%single.entity-type'),
			description: t('single.filter.notifications-for.resource'),
			target: () => document.getElementById('target-filter-entity-table')
		},
		{
			title: t('__filter-by-x%single.kind'),
			description: t('single.filter.notifications-for.action'),
			target: () => document.getElementById('target-filter-kind')
		}
	]

	/**
	 * @function startTour
	 * @description Initializes and starts the guided UI tour for the notification list screen. Injects a demo item if the list is empty.
	 */
	const startTour = () => {
		const hasItems = uiLstNotifications.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() => {
				runInAction(() => {
					uiLstNotifications.setIds(
						uiLstNotifications.ids.filter((id) => id !== newId)
					)
					uiLstNotifications.setNr(uiLstNotifications.nr - 1)
				})
			})

			const demoObj = {
				for_user_id: loggedInUserId,
				entity_table: 'sprints',
				entity_uid: '123',
				kind: 'new',
				progress: 'not_read'
			}
			actSaveNotification(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('notificationLst-tour', [
			...tourFilter,
			...notificationLst
		])
	}

	const hideFilters = []
	if (
		!(
			isAdmin ||
			isSuperAdmin ||
			users.canUser('delete_view', 'notifications')
		)
	) {
		hideFilters.push('show_all')
	} else {
		tourFilter.push({
			title: t('__show-all-x%_filter'),
			description: t('single.filter.all-notifications'),
			target: () => document.getElementById('target-filter-show-all')
		})
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_notification.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<Filter
				uiStore={uiLstNotifications}
				isForNotification={true}
				hide={[
					...hideFilters,
					'search',
					'priority',
					'category',
					'addNew',
					'by-user',
					'for-user',
					'orderBy',
					'deleted'
				]}
				progressOptions={[
					{
						key: 'by-progress',
						value: '',
						label: t('__by%single.status')
					},
					{
						key: 'by-progress-read',
						value: 'read',
						label: t('single.read')
					},
					{
						key: 'by-progress-not_read',
						value: 'not_read',
						label: t('single.not-read')
					}
				]}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstNotifications}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-notification">{t('_item')}</th>
					<th id="target-table">{t('_table')}</th>
					<th id="target-info">{t('single.info')}</th>
					<th id="target-actions">{t('_action.multiple')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})
export default NotificationsLst
